import { OffsetOptions, Placement, Strategy } from '@floating-ui/core'
import { FlipOptions } from '@floating-ui/dom'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import React, { useState } from 'react'

interface usePopoverPropertiesProps {
  offsetValue: OffsetOptions
  flipOptions: FlipOptions
  strategyValue: Strategy
  placement: Placement
  ignoredEventTypes?: Event['type'][]
  isOpen?: boolean
  setIsOpen?: (isOpen: boolean) => void
}

const usePopoverProperties = ({
  offsetValue,
  flipOptions,
  strategyValue,
  placement,
  ignoredEventTypes = [],
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
}: usePopoverPropertiesProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
  const setIsOpen = externalSetIsOpen || setInternalIsOpen

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open, event) => {
      // either explicit call (no event) or allowed event type
      if (!(event && ignoredEventTypes.includes(event.type))) {
        setIsOpen(open)
      }
    },
    placement: placement,
    middleware: [offset(offsetValue), flip(flipOptions), shift()],
    strategy: strategyValue,
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        layoutShift: false,
        elementResize: false,
      }),
  })
  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ])

  return React.useMemo(
    () => ({
      refs,
      floatingStyles: {
        ...floatingStyles,
        zIndex: 'var(--z-index-popover)',
      },
      getReferenceProps,
      getFloatingProps,
      isOpen,
      context,
    }),
    [
      refs,
      floatingStyles,
      getReferenceProps,
      getFloatingProps,
      isOpen,
      context,
    ],
  )
}

export default usePopoverProperties
